import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { GET_CURRENT_USER_REQUEST, LOGOUT_REQUEST, getFetchRequest, postFetchRequest } from '../store/requests';
import { makeUrl } from '../util';
import { setCurrentUser } from '../services/currentUserStore';

export type AuthUser = {
    id: string;
    locale?: string;
    name?: string;
    email?: string;
    phone?: string;
    timezone?: string;
    appRole?: {
        id?: string;
        code?: string;
        name?: string;
    };
};

type AuthContextValue = {
    user: AuthUser | null;
    loading: boolean;
    refresh: () => Promise<AuthUser | null>;
    setUser: (user: AuthUser | null) => void;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUserState] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    const setUser = useCallback((next: AuthUser | null) => {
        setUserState(next);
        setCurrentUser(next);
    }, []);

    const refresh = useCallback(() => new Promise<AuthUser | null>((resolve) => {
        getFetchRequest(
            makeUrl([GET_CURRENT_USER_REQUEST]),
            async (response: Response) => {
                if (response.status === 200) {
                    try {
                        const result = await response.json() as AuthUser;
                        if (result?.id) {
                            setUser(result);
                            resolve(result);
                            return;
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
                setUser(null);
                resolve(null);
            },
            (err: unknown) => {
                console.error(err);
                setUser(null);
                resolve(null);
            },
            true,
        );
    }), [setUser]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void refresh().finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [refresh]);

    const logout = useCallback(() => new Promise<void>((resolve) => {
        postFetchRequest(
            makeUrl([LOGOUT_REQUEST]),
            null,
            () => {
                setUser(null);
                resolve();
            },
            () => {
                setUser(null);
                resolve();
            },
            true,
        );
    }), [setUser]);

    const value = useMemo(
        () => ({ user, loading, refresh, setUser, logout }),
        [user, loading, refresh, setUser, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}

export function getSafeNextPath(searchParams: URLSearchParams, fallback = '/'): string {
    const raw = searchParams.get('next') || fallback;
    return raw.startsWith('/') ? raw : fallback;
}
